import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, RefreshCw, Loader2, UserX, Check, Circle, Eye, EyeOff, Lock } from 'lucide-react';
import KkovSelect from '../../KkovSelect';
import ModalShell from '../../ui/ModalShell';
import KDoreseniRadek from '../../layout/KDoreseni';
import {
    SECTION_COLOR, STAV_LABEL, ONBOARDING_KROKY, datum, celeJmeno,
    pozadovaneCertifikaty, vNastupu,
} from '../lide';
import {
    Sekce, SekceAkce, Radky, Radek, Pole, Formular, Vstup, Vyber, Zaskrtavatko,
    TlacitkaFormulare, Tlacitko, Hlaska, muted, linka, inputStyl,
} from './ui';
import { Certifikat } from './ZamestnanecPanel';
import KarieraMzda from './KarieraMzda';
import DokumentyZamestnance from './DokumentyZamestnance';
import ZpetTlacitko from '../../ui/ZpetTlacitko';

/**
 * Karta zaměstnance — DETAIL modulu Lidé (druhý sloupec `ModulLayout`).
 *
 * Dřív to byl vlastní modal 720 px přes seznam (`ZamestnanecModal`): portál se
 * `zIndex 9999`, vlastní pozadí do modra, oranžový akcent, sekce jako karty
 * v kartě s ikonami v osmi barvách. Mezi lidmi se nedalo proklikat — každého
 * bylo nutné zavřít. Teď stojí vedle zúženého seznamu (norma §1), na mobilu
 * na celé obrazovce se „Zpět" (§1b).
 *
 * Citlivé údaje (rodné číslo, doklady, bydliště, zdraví, exekuce, vyživované
 * osoby) jsou zamaskované a odkryje je až „Zobrazit" — při proklikávání lidí
 * nesvítí na obrazovce. Po přepnutí na dalšího člověka se schovají znovu.
 *
 * Karta odpovídá na **„co o něm evidujeme"** — všechno v ní jde upravit. Co
 * jen informuje (fotka, jak dlouho je ve firmě, certifikace), nese třetí
 * sloupec, a proto to tady s `maPanel` není podruhé. Bez panelu (úzké okno,
 * telefon) se to sem vrátí — jinak by to nebylo vidět nikde.
 */

const RODINNY_STAV = ['Svobodný/á', 'Ženatý/Vdaná', 'Rozvedený/á', 'Vdovec/Vdova', 'Druh/Družka'];
const VZDELANI = ['Základní', 'Střední odborné (SOU)', 'Střední s maturitou', 'Vyšší odborné (VOS)', 'Vysokoškolské'];
const DUCHODY = ['Starobní', 'Invalidní I. stupně', 'Invalidní II. stupně', 'Invalidní III. stupně'];
const ZDR_OMEZENI = ['I', 'II', 'III', 'OZZ'];
const MASKA = '••••••';

const vOpcich = (pole) => pole.map(o => ({ value: o, label: o }));

// ── Rodné číslo → datum narození + pohlaví ─────────
function parseRodneCislo(rc) {
    if (!rc) return null;
    const clean = rc.replace(/[\s/]/g, '');
    if (!/^\d{9,10}$/.test(clean)) return null;
    let year = parseInt(clean.substring(0, 2), 10);
    let month = parseInt(clean.substring(2, 4), 10);
    const day = parseInt(clean.substring(4, 6), 10);
    let pohlavi = 'muz';
    if (month > 70) { pohlavi = 'zena'; month -= 70; }
    else if (month > 50) { pohlavi = 'zena'; month -= 50; }
    else if (month > 20) { month -= 20; }
    if (clean.length === 9) year += 1900;
    else year += year < 54 ? 2000 : 1900;
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    if (clean.length === 10 && parseInt(clean, 10) % 11 !== 0) return null;
    return { datum: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`, pohlavi };
}

const iso = (v) => (v ? String(v).split('T')[0] : '');

function formularZ(d) {
    return {
        jmeno: d.jmeno || '', prijmeni: d.prijmeni || '', email: d.email || '', telefon: d.telefon || '',
        typ_uvazku_id: d.typ_uvazku_id || '', datum_nastupu: iso(d.datum_nastupu),
        datum_vystupu: iso(d.datum_vystupu), datum_narozeni: iso(d.datum_narozeni),
        nadrizeny_id: d.nadrizeny_id || '', stav: d.stav || 'aktivni',
        poznamka: d.poznamka || '', pozice_id: d.pozice_id || '', oddeleni_ids: d.oddeleni?.map(o => o.id) || [],
        rodne_cislo: d.rodne_cislo || '', cislo_dokladu: d.cislo_dokladu || '',
        cislo_ridicskeho_prukazu: d.cislo_ridicskeho_prukazu || '',
        statni_prislusnost_id: d.statni_prislusnost_id || '', pojistovna_id: d.pojistovna_id || '',
        bankovni_ucet: d.bankovni_ucet || '', adresa_ulice: d.adresa_ulice || '',
        adresa_mesto: d.adresa_mesto || '', adresa_psc: d.adresa_psc || '',
        adresa_stat: d.adresa_stat || 'Česká republika', titul_pred: d.titul_pred || '',
        titul_za: d.titul_za || '', kontaktni_adresa_ulice: d.kontaktni_adresa_ulice || '',
        kontaktni_adresa_mesto: d.kontaktni_adresa_mesto || '', kontaktni_adresa_psc: d.kontaktni_adresa_psc || '',
        kontaktni_adresa_stat: d.kontaktni_adresa_stat || '', misto_narozeni: d.misto_narozeni || '',
        pohlavi: d.pohlavi || '', rodne_prijmeni: d.rodne_prijmeni || '', narodnost: d.narodnost || '',
        rodinny_stav: d.rodinny_stav || '', vzdelani: d.vzdelani || '', obor_vzdelani: d.obor_vzdelani || '',
        vzdelani_kod: d.vzdelani_kod || '', typ_duchodu: d.typ_duchodu || '',
        datum_duchodu: iso(d.datum_duchodu), predcasny_duchod: !!d.predcasny_duchod,
        snizeny_vek: !!d.snizeny_vek, insolvence_exekuce: d.insolvence_exekuce || 'nemam',
        zdravotni_omezeni_typ: d.zdravotni_omezeni_typ || '',
        zdravotni_omezeni_od: iso(d.zdravotni_omezeni_od), zdravotni_omezeni_do: iso(d.zdravotni_omezeni_do),
        ztp_p: !!d.ztp_p,
    };
}

/**
 * Maskovaná pole sekce jedním řádkem, dokud je nikdo neodkryl.
 *
 * Šest řádků `••••••` vypadá jako data a přitom neříká nic — zaberou přesně
 * tolik místa jako šest vyplněných hodnot. Dokud jsou údaje skryté, stačí
 * vědět, co v nich je; „Zobrazit" je rozbalí do plné mřížky i s patičkou
 * nevyplněných.
 */
function SkrytaPole({ pole, isDark }) {
    const vyplnena = pole.filter(([, v]) => v).map(([popis]) => popis);
    return (
        <div className="flex items-center" style={{ gap: 10, padding: '9px 0 10px', borderBottom: `1px solid ${linka(isDark)}` }}>
            <Lock size={15} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
            <span className="text-[13px] min-w-0" style={{ color: vyplnena.length ? 'var(--text-primary)' : muted(isDark) }}>
                {vyplnena.length ? `Vyplněno: ${vyplnena.join(', ')}` : 'Zatím nic vyplněného'}
            </span>
        </div>
    );
}

const kontaktniShodna = (d) => {
    const prazdna = !d.kontaktni_adresa_ulice && !d.kontaktni_adresa_mesto && !d.kontaktni_adresa_psc && !d.kontaktni_adresa_stat;
    const stejna = d.kontaktni_adresa_ulice === d.adresa_ulice && d.kontaktni_adresa_mesto === d.adresa_mesto
        && d.kontaktni_adresa_psc === d.adresa_psc && d.kontaktni_adresa_stat === d.adresa_stat;
    return prazdna || stejna;
};

export default function ZamestnanecKarta({
    zamestnanecId, user, isDark, isMobile, maPanel = false,
    certTypy = [], vsichniZamestnanci = [],
    kDoreseni = [], renderAkceKDoreseni = null, prehledNaMobilu = null,
    rezervaDole = null, onZpet, onDataChanged,
}) {
    const [z, setZ] = useState(null);
    const [tab, setTab] = useState('karta');
    const [upravuje, setUpravuje] = useState(null);       // id sekce v editaci
    const [form, setForm] = useState({});
    const [uklada, setUklada] = useState(false);
    const [kontaktniStejna, setKontaktniStejna] = useState(false);
    const [odkryto, setOdkryto] = useState(false);        // citlivé údaje
    const [muzuEditovat, setMuzuEditovat] = useState(false);
    const [cis, setCis] = useState({ uvazky: [], pozice: [], oddeleni: [], pojistovny: [], staty: [] });
    const [dotaznik, setDotaznik] = useState({ bezi: false, hlaska: null });
    const [deaktivace, setDeaktivace] = useState(false);
    const [pocetDokumentu, setPocetDokumentu] = useState(null);

    const setPole = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const vlastni = user?.zamestnanec_id === zamestnanecId;
    const smiOdkryt = muzuEditovat || vlastni;

    const nacti = useCallback(() => fetch(`/api/zamestnanci/${zamestnanecId}`)
        .then(r => r.json())
        .then(d => { setZ(d); setForm(formularZ(d)); setKontaktniStejna(kontaktniShodna(d)); }), [zamestnanecId]);

    // Jiný člověk = čistý stav: bez rozpracované editace, citlivé údaje zase schované.
    useEffect(() => {
        setZ(null); setTab('karta'); setUpravuje(null); setOdkryto(false);
        setDotaznik({ bezi: false, hlaska: null }); setDeaktivace(false); setPocetDokumentu(null);
        nacti();
        fetch(`/api/zamestnanci/${zamestnanecId}/muzu-editovat`).then(r => r.json())
            .then(d => setMuzuEditovat(!!d.muzu_editovat)).catch(() => setMuzuEditovat(false));
    }, [zamestnanecId, nacti]);

    // Číselníky jen jednou — nemění se mezi lidmi.
    useEffect(() => {
        const j = (u) => fetch(u).then(r => r.json()).catch(() => []);
        Promise.all([
            j('/api/ciselniky/typy-uvazku'), j('/api/ciselniky/pozice'), j('/api/ciselniky/oddeleni'),
            j('/api/ciselniky/pojistovny'), j('/api/ciselniky/statni-prislusnosti'),
        ]).then(([uvazky, pozice, oddeleni, pojistovny, staty]) => setCis({ uvazky, pozice, oddeleni, pojistovny, staty }));
    }, []);

    const zacniUpravu = (sekce) => {
        setForm(formularZ(z)); setKontaktniStejna(kontaktniShodna(z));
        setUpravuje(sekce); setOdkryto(true);
    };

    const uloz = async (pole) => {
        setUklada(true);
        const payload = {};
        pole.forEach(k => { payload[k] = form[k]; });
        if (pole.includes('adresa_ulice') && kontaktniStejna) {
            payload.kontaktni_adresa_ulice = form.adresa_ulice;
            payload.kontaktni_adresa_mesto = form.adresa_mesto;
            payload.kontaktni_adresa_psc = form.adresa_psc;
            payload.kontaktni_adresa_stat = form.adresa_stat;
        }
        const res = await fetch(`/api/zamestnanci/${zamestnanecId}`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(payload),
        });
        if (res.ok) { setUpravuje(null); await nacti(); onDataChanged?.(); }
        setUklada(false);
    };

    const novyDotaznik = async () => {
        if (dotaznik.bezi) return;
        setDotaznik({ bezi: true, hlaska: null });
        let hlaska;
        try {
            const res = await fetch(`/api/zamestnanci/${zamestnanecId}/regenerovat-dotaznik`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            });
            hlaska = res.ok ? { typ: 'ok', text: 'Dotazník vygenerován a odeslán účetní' } : { typ: 'chyba', text: 'Nepodařilo se vygenerovat' };
        } catch {
            hlaska = { typ: 'chyba', text: 'Chyba při generování' };
        }
        setDotaznik({ bezi: false, hlaska });
        setTimeout(() => setDotaznik(d => ({ ...d, hlaska: null })), 4000);
    };

    const mut = muted(isDark);

    if (!z) {
        return (
            <div className="flex items-center justify-center flex-1" style={{ minHeight: 200, color: mut }}>
                <Loader2 size={20} className="animate-spin" />
            </div>
        );
    }

    const nadrizeny = z.nadrizeny_id
        ? celeJmeno(vsichniZamestnanci.find(v => v.id === z.nadrizeny_id)) || celeJmeno(z.nadrizeny) || null
        : null;
    const citlive = (v) => (odkryto ? v : (v ? MASKA : null));
    const upravit = (sekce) => (muzuEditovat && upravuje !== sekce
        ? <SekceAkce isMobile={isMobile} onClick={() => zacniUpravu(sekce)}>Upravit</SekceAkce>
        : null);
    const zrusit = () => setUpravuje(null);
    const tl = (pole) => (
        <TlacitkaFormulare isDark={isDark} isMobile={isMobile} uklada={uklada} onUlozit={() => uloz(pole)} onZrusit={zrusit} />
    );
    // S panelem vedle sebe nese podtitulek jen pozici — oddělení, úvazek
    // i nástup stojí o kus níž v Pracovních údajích, kde se i mění.
    const meta = (maPanel ? [z.pozice?.nazev] : [
        z.pozice?.nazev,
        z.oddeleni?.length ? `oddělení ${z.oddeleni.map(o => o.nazev).join(', ')}` : null,
        z.typ_uvazku?.kod && z.typ_uvazku.kod !== 'ZADNY' ? z.typ_uvazku.kod : null,
        z.datum_nastupu ? `nástup ${datum(z.datum_nastupu)}` : null,
        !isMobile && nadrizeny ? `nadřízený ${nadrizeny}` : null,
    ]).filter(Boolean).join(' · ');
    const avatar = z.uzivatel?.avatar_custom_url || z.avatar_url;
    const certKody = [...new Set([...pozadovaneCertifikaty(z), ...(z.certifikace || []).map(c => c.typ)])];

    const zalozky = [
        { id: 'karta', label: 'Karta' },
        { id: 'kariera', label: 'Kariéra a mzda' },
        { id: 'dokumenty', label: 'Dokumenty', pocet: pocetDokumentu },
    ];

    // ═══ Hlavička — kdo to je, cesta ven, akce (norma §1e: max tři ovladače) ═══
    const hlavicka = (
        <div style={{ padding: isMobile ? '6px 12px 0' : '14px 18px 0', borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`, flexShrink: 0 }}>
            {isMobile && (
                <ZpetTlacitko onClick={onZpet} popis="Zpět na seznam lidí" sklo isDark={isDark} />
            )}
            <div className="flex items-center" style={{ gap: 14 }}>
                {/* Tvář má karta vždycky — v panelu jako portrét, bez panelu tady. */}
                {!maPanel && (
                    avatar ? (
                        <img src={avatar.startsWith('/') ? avatar : `/storage/${avatar}`} alt=""
                            style={{ width: 52, height: 52, borderRadius: '50%', objectFit: 'cover', objectPosition: z.uzivatel?.avatar_position || z.avatar_position || 'center', flexShrink: 0 }} />
                    ) : (
                        <div className="flex items-center justify-center font-bold"
                            style={{ width: 52, height: 52, borderRadius: '50%', background: `${SECTION_COLOR}24`, color: SECTION_COLOR, fontSize: 17, flexShrink: 0 }}>
                            {z.jmeno?.[0]}{z.prijmeni?.[0]}
                        </div>
                    )
                )}
                <div className="min-w-0 flex-1">
                    <div className="flex items-center flex-wrap" style={{ gap: 8 }}>
                        <h2 className="font-bold truncate" style={{ fontSize: isMobile ? 18 : 19, color: 'var(--text-primary)' }}>
                            {[z.titul_pred, celeJmeno(z), z.titul_za].filter(Boolean).join(' ')}
                        </h2>
                        <span className="text-[11px] font-semibold rounded-full"
                            style={{
                                padding: '2px 8px',
                                ...(z.stav === 'aktivni'
                                    ? { color: '#34d399', background: 'rgba(5,150,105,0.12)', border: '1px solid rgba(5,150,105,0.35)' }
                                    : { color: 'var(--text-secondary)', background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}` }),
                            }}>
                            {STAV_LABEL[z.stav] || z.stav}
                        </span>
                    </div>
                    {meta && <p className="text-[12.5px]" style={{ color: 'var(--text-secondary)', marginTop: 2 }}>{meta}</p>}
                </div>
                {muzuEditovat && !isMobile && (
                    <div className="flex flex-col items-end">
                        <Tlacitko isDark={isDark} onClick={novyDotaznik} disabled={dotaznik.bezi}
                            title="Vygenerovat nový osobní dotazník a odeslat ho účetní">
                            <RefreshCw size={14} className={dotaznik.bezi ? 'animate-spin' : ''} /> Nový dotazník
                        </Tlacitko>
                        <Hlaska hlaska={dotaznik.hlaska} />
                    </div>
                )}
            </div>

            {/* Mobil: co bylo ve třetím sloupci, je za tímhle řádkem (panel se na telefonu nekreslí) */}
            {isMobile && kDoreseni.length > 0 && (
                <div style={{ marginTop: 10 }}>
                    <KDoreseniRadek polozky={kDoreseni} isDark={isDark} barva={SECTION_COLOR}
                        renderAkce={renderAkceKDoreseni} navic={prehledNaMobilu} />
                </div>
            )}

            <div role="tablist" className="flex overflow-x-auto" style={{ gap: 2, marginTop: isMobile ? 6 : 12, scrollbarWidth: 'none' }}>
                {zalozky.map(t => {
                    const aktivni = tab === t.id;
                    return (
                        <button key={t.id} type="button" role="tab" aria-selected={aktivni} onClick={() => setTab(t.id)}
                            className="whitespace-nowrap"
                            style={{
                                height: isMobile ? 44 : 38, padding: '0 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                                background: 'none', border: 'none', marginBottom: -1,
                                color: aktivni ? SECTION_COLOR : 'var(--text-secondary)',
                                borderBottom: `2px solid ${aktivni ? SECTION_COLOR : 'transparent'}`,
                            }}>
                            {t.label}
                            {t.pocet != null && <span style={{ color: mut, fontWeight: 500, marginLeft: 5 }}>{t.pocet}</span>}
                        </button>
                    );
                })}
            </div>
        </div>
    );

    // ═══ Záložka Karta ═══
    const onboardingKarty = (() => {
        const cl = z.onboarding_checklist;
        if (!cl || !vNastupu(z)) return null;
        const hotovo = ONBOARDING_KROKY.filter(k => cl[k.key]).length;
        return (
            <Sekce nazev={`Nástup · ${hotovo}/${ONBOARDING_KROKY.length}`} isDark={isDark} prvni>
                <div style={{ height: 5, borderRadius: 3, background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', margin: '6px 0 10px' }}>
                    <div style={{ height: '100%', borderRadius: 3, width: `${(hotovo / ONBOARDING_KROKY.length) * 100}%`, background: SECTION_COLOR }} />
                </div>
                <div className="flex flex-wrap" style={{ gap: '6px 14px' }}>
                    {ONBOARDING_KROKY.map(k => (
                        <span key={k.key} className="flex items-center gap-1 text-[12px]" style={{ color: cl[k.key] ? 'var(--text-primary)' : mut }}>
                            {cl[k.key] ? <Check size={13} style={{ color: '#34d399' }} /> : <Circle size={12} />}
                            {k.label}
                        </span>
                    ))}
                </div>
                {z.onboarding_hotovo && z.stav !== 'aktivni' && (
                    <p className="text-[12px]" style={{ color: 'var(--text-secondary)', marginTop: 8 }}>
                        Vše splněno — účet se aktivuje po dokončení posledního kroku.
                    </p>
                )}
            </Sekce>
        );
    })();

    const karta = (
        <>
            {onboardingKarty}

            {/* S panelem vedle sebe je certifikace tam. Bez něj sem — stejnou
                komponentou, ať se obě podoby nerozejdou u první změny. */}
            {!maPanel && certKody.length > 0 && (
                <Sekce nazev="Certifikace" isDark={isDark} prvni={!onboardingKarty}>
                    {/* Do mřížky jako ostatní hodnoty — přes celou kartu by měl
                        pruh platnosti přes 700 px a přestal by se dát odhadnout. */}
                    <Radky isMobile={isMobile}>
                        {certKody.map(kod => (
                            <Certifikat key={kod} z={z} kod={kod} typ={certTypy.find(t => t.kod === kod)} isDark={isDark} />
                        ))}
                    </Radky>
                </Sekce>
            )}

            {(z.kariery || []).length > 0 && (
                <Sekce nazev="Může pracovat jako" isDark={isDark} prvni={!onboardingKarty && (maPanel || certKody.length === 0)}>
                    <div className="flex flex-wrap" style={{ gap: 6, padding: '4px 0' }}>
                        {z.kariery.map(k => {
                            const u = z.urovne?.find(x => (x.kariera_id || x.kariera?.id) === k.id);
                            const uroven = u?.uroven?.nazev || 'Novice';
                            const sazba = uroven === 'Leader' ? 'fixní plat' : u?.sazba_kc_h ? `${u.sazba_kc_h} Kč/h` : null;
                            return (
                                <span key={k.id} className="inline-flex items-center rounded-lg text-[12.5px]"
                                    style={{ gap: 6, padding: '6px 10px', border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}` }}>
                                    <b style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{k.nazev}</b>
                                    <span style={{ color: mut }}>{uroven}{sazba ? ` · ${sazba}` : ''}</span>
                                </span>
                            );
                        })}
                    </div>
                </Sekce>
            )}

            <Sekce nazev="Pracovní údaje" isDark={isDark} akce={upravit('prace')}>
                {upravuje === 'prace' ? (
                    <Formular isMobile={isMobile}>
                        <Pole label="Typ úvazku" isDark={isDark}>
                            <Vyber isDark={isDark} value={form.typ_uvazku_id} onChange={v => setPole('typ_uvazku_id', v)}
                                options={cis.uvazky.map(t => ({ value: t.id, label: `${t.nazev} (${t.kod})` }))} />
                        </Pole>
                        <Pole label="Datum nástupu" isDark={isDark}>
                            <Vstup isDark={isDark} type="date" value={form.datum_nastupu} onChange={e => setPole('datum_nastupu', e.target.value)} />
                        </Pole>
                        <Pole label="Nadřízený" isDark={isDark}>
                            <Vyber isDark={isDark} value={form.nadrizeny_id} onChange={v => setPole('nadrizeny_id', v)} placeholder="— bez nadřízeného —"
                                options={vsichniZamestnanci.filter(v => v.id !== z.id).map(v => ({ value: v.id, label: celeJmeno(v) }))} />
                        </Pole>
                        <Pole label="Pozice" isDark={isDark}>
                            <Vyber isDark={isDark} value={form.pozice_id} onChange={v => setPole('pozice_id', v || null)}
                                options={cis.pozice.map(p => ({ value: p.id, label: p.nazev }))} />
                        </Pole>
                        <Pole label="Oddělení" isDark={isDark} siroky>
                            <div className="flex flex-wrap" style={{ gap: 6 }}>
                                {cis.oddeleni.map(o => {
                                    const sel = (form.oddeleni_ids || []).includes(o.id);
                                    return (
                                        <button key={o.id} type="button"
                                            onClick={() => setPole('oddeleni_ids', sel ? form.oddeleni_ids.filter(id => id !== o.id) : [...(form.oddeleni_ids || []), o.id])}
                                            className="rounded-lg text-[13px]"
                                            style={{
                                                height: isMobile ? 40 : 32, padding: '0 12px', cursor: 'pointer',
                                                background: sel ? `${SECTION_COLOR}1f` : 'transparent',
                                                color: sel ? SECTION_COLOR : 'var(--text-secondary)',
                                                border: `1px solid ${sel ? `${SECTION_COLOR}88` : (isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)')}`,
                                            }}>
                                            {o.nazev}
                                        </button>
                                    );
                                })}
                            </div>
                        </Pole>
                        {tl(['typ_uvazku_id', 'datum_nastupu', 'nadrizeny_id', 'pozice_id', 'oddeleni_ids'])}
                    </Formular>
                ) : (
                    <Radky isMobile={isMobile} isDark={isDark} skryjPrazdne>
                        <Radek isDark={isDark} isMobile={isMobile} popis="Typ úvazku" hodnota={z.typ_uvazku?.nazev} />
                        <Radek isDark={isDark} isMobile={isMobile} popis="Pozice" hodnota={z.pozice?.nazev} />
                        <Radek isDark={isDark} isMobile={isMobile} popis="Datum nástupu" hodnota={datum(z.datum_nastupu)} />
                        <Radek isDark={isDark} isMobile={isMobile} popis="Datum výstupu" hodnota={datum(z.datum_vystupu)} />
                        <Radek isDark={isDark} isMobile={isMobile} popis="Nadřízený" hodnota={nadrizeny} />
                        <Radek isDark={isDark} isMobile={isMobile} popis="Oddělení" hodnota={z.oddeleni?.map(o => o.nazev).join(', ')} />
                    </Radky>
                )}
            </Sekce>

            <Sekce nazev="Kontakt" isDark={isDark} akce={upravit('kontakt')}>
                {upravuje === 'kontakt' ? (
                    <Formular isMobile={isMobile}>
                        <Pole label="E-mail" isDark={isDark}><Vstup isDark={isDark} type="email" value={form.email} onChange={e => setPole('email', e.target.value)} /></Pole>
                        <Pole label="Telefon" isDark={isDark}><Vstup isDark={isDark} value={form.telefon} onChange={e => setPole('telefon', e.target.value)} /></Pole>
                        {tl(['email', 'telefon'])}
                    </Formular>
                ) : (
                    <Radky isMobile={isMobile}>
                        <Radek isDark={isDark} isMobile={isMobile} popis="Telefon"
                            hodnota={z.telefon ? <a href={`tel:${z.telefon}`} style={{ color: 'inherit' }}>{z.telefon}</a> : null} />
                        <Radek isDark={isDark} isMobile={isMobile} popis="E-mail"
                            hodnota={z.email ? <a href={`mailto:${z.email}`} style={{ color: 'inherit' }}>{z.email}</a> : null} />
                    </Radky>
                )}
            </Sekce>

            {/* ── Citlivé údaje: jeden přepínač pro všechny sekce níž ── */}
            <div className="flex items-center justify-between" style={{ marginTop: 22, paddingTop: 10, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                <span className="text-[12px]" style={{ color: mut }}>
                    {odkryto ? 'Osobní údaje jsou odkryté' : 'Osobní údaje jsou skryté'}
                </span>
                {smiOdkryt && (
                    <Tlacitko isDark={isDark} vyska={isMobile ? 40 : 30} onClick={() => setOdkryto(o => !o)}>
                        {odkryto ? <><EyeOff size={14} /> Skrýt</> : <><Eye size={14} /> Zobrazit</>}
                    </Tlacitko>
                )}
            </div>

            <Sekce nazev="Osobní údaje" isDark={isDark} akce={upravit('osobni')}>
                {upravuje === 'osobni' ? (
                    <Formular isMobile={isMobile}>
                        <Pole label="Titul před jménem" isDark={isDark}><Vstup isDark={isDark} value={form.titul_pred} onChange={e => setPole('titul_pred', e.target.value)} placeholder="Ing., Bc., …" /></Pole>
                        <Pole label="Titul za jménem" isDark={isDark}><Vstup isDark={isDark} value={form.titul_za} onChange={e => setPole('titul_za', e.target.value)} placeholder="Ph.D., MBA, …" /></Pole>
                        <Pole label="Jméno" isDark={isDark}><Vstup isDark={isDark} value={form.jmeno} onChange={e => setPole('jmeno', e.target.value)} /></Pole>
                        <Pole label="Příjmení" isDark={isDark}><Vstup isDark={isDark} value={form.prijmeni} onChange={e => setPole('prijmeni', e.target.value)} /></Pole>
                        <Pole label="Rodné číslo" isDark={isDark}>
                            <Vstup isDark={isDark} value={form.rodne_cislo} placeholder="123456/7890"
                                onChange={e => {
                                    const v = e.target.value;
                                    const p = parseRodneCislo(v);
                                    setForm(f => ({ ...f, rodne_cislo: v, ...(p ? { datum_narozeni: p.datum, pohlavi: p.pohlavi } : {}) }));
                                }} />
                            {parseRodneCislo(form.rodne_cislo) && <span className="text-[11px] block" style={{ color: mut, marginTop: 3 }}>Datum narození a pohlaví dopočteny z RČ</span>}
                        </Pole>
                        <Pole label="Datum narození" isDark={isDark}>
                            <Vstup isDark={isDark} type="date" value={form.datum_narozeni} onChange={e => setPole('datum_narozeni', e.target.value)}
                                readOnly={!!parseRodneCislo(form.rodne_cislo)} style={parseRodneCislo(form.rodne_cislo) ? { opacity: 0.6 } : null} />
                        </Pole>
                        <Pole label="Pohlaví" isDark={isDark}>
                            {parseRodneCislo(form.rodne_cislo)
                                ? <p className="text-sm" style={{ padding: '8px 0', color: 'var(--text-primary)' }}>{form.pohlavi === 'muz' ? 'Muž' : 'Žena'} <span style={{ color: mut }}>(z RČ)</span></p>
                                : <Vyber isDark={isDark} value={form.pohlavi} onChange={v => setPole('pohlavi', v)} options={[{ value: 'muz', label: 'Muž' }, { value: 'zena', label: 'Žena' }]} />}
                        </Pole>
                        <Pole label="Místo narození" isDark={isDark}><Vstup isDark={isDark} value={form.misto_narozeni} onChange={e => setPole('misto_narozeni', e.target.value)} /></Pole>
                        <Pole label="Rodné příjmení" isDark={isDark}><Vstup isDark={isDark} value={form.rodne_prijmeni} onChange={e => setPole('rodne_prijmeni', e.target.value)} placeholder="Pokud se liší" /></Pole>
                        <Pole label="Národnost" isDark={isDark}><Vstup isDark={isDark} value={form.narodnost} onChange={e => setPole('narodnost', e.target.value)} placeholder="Česká" /></Pole>
                        {tl(['titul_pred', 'jmeno', 'prijmeni', 'titul_za', 'rodne_cislo', 'datum_narozeni', 'pohlavi', 'misto_narozeni', 'rodne_prijmeni', 'narodnost'])}
                    </Formular>
                ) : (
                    <>
                        {!odkryto && (
                            <SkrytaPole isDark={isDark} pole={[
                                ['rodné číslo', z.rodne_cislo], ['datum narození', z.datum_narozeni],
                                ['místo narození', z.misto_narozeni], ['rodné příjmení', z.rodne_prijmeni],
                            ]} />
                        )}
                        <Radky isMobile={isMobile} isDark={isDark} skryjPrazdne>
                            {odkryto && <Radek isDark={isDark} isMobile={isMobile} popis="Rodné číslo" hodnota={citlive(z.rodne_cislo)} mono />}
                            {odkryto && <Radek isDark={isDark} isMobile={isMobile} popis="Datum narození"
                                hodnota={citlive(z.datum_narozeni ? `${datum(z.datum_narozeni)} (${new Date().getFullYear() - parseInt(iso(z.datum_narozeni).split('-')[0], 10)} let)` : null)} />}
                            <Radek isDark={isDark} isMobile={isMobile} popis="Pohlaví" hodnota={z.pohlavi === 'muz' ? 'Muž' : z.pohlavi === 'zena' ? 'Žena' : null} />
                            {odkryto && <Radek isDark={isDark} isMobile={isMobile} popis="Místo narození" hodnota={citlive(z.misto_narozeni)} />}
                            {odkryto && <Radek isDark={isDark} isMobile={isMobile} popis="Rodné příjmení" hodnota={citlive(z.rodne_prijmeni)} />}
                            <Radek isDark={isDark} isMobile={isMobile} popis="Národnost" hodnota={z.narodnost} />
                        </Radky>
                    </>
                )}
            </Sekce>

            <Sekce nazev="Doklady a účet" isDark={isDark} akce={upravit('doklady')}>
                {upravuje === 'doklady' ? (
                    <Formular isMobile={isMobile}>
                        <Pole label="Číslo dokladu (OP/pas)" isDark={isDark}><Vstup isDark={isDark} value={form.cislo_dokladu} onChange={e => setPole('cislo_dokladu', e.target.value)} /></Pole>
                        <Pole label="Číslo ŘP" isDark={isDark}><Vstup isDark={isDark} value={form.cislo_ridicskeho_prukazu} onChange={e => setPole('cislo_ridicskeho_prukazu', e.target.value)} /></Pole>
                        <Pole label="Státní příslušnost" isDark={isDark}>
                            <Vyber isDark={isDark} value={form.statni_prislusnost_id} onChange={v => setPole('statni_prislusnost_id', v || null)}
                                options={cis.staty.map(s => ({ value: s.id, label: s.nazev }))} />
                        </Pole>
                        <Pole label="Zdravotní pojišťovna" isDark={isDark}>
                            <Vyber isDark={isDark} value={form.pojistovna_id} onChange={v => setPole('pojistovna_id', v || null)}
                                options={cis.pojistovny.map(p => ({ value: p.id, label: `${p.kod} – ${p.nazev}` }))} />
                        </Pole>
                        <Pole label="Bankovní účet" isDark={isDark}><Vstup isDark={isDark} value={form.bankovni_ucet} onChange={e => setPole('bankovni_ucet', e.target.value)} placeholder="123456789/0100" /></Pole>
                        {tl(['cislo_dokladu', 'cislo_ridicskeho_prukazu', 'statni_prislusnost_id', 'pojistovna_id', 'bankovni_ucet'])}
                    </Formular>
                ) : (
                    <>
                        {!odkryto && (
                            <SkrytaPole isDark={isDark} pole={[
                                ['číslo dokladu', z.cislo_dokladu], ['číslo ŘP', z.cislo_ridicskeho_prukazu],
                                ['zdravotní pojišťovna', z.pojistovna], ['bankovní účet', z.bankovni_ucet],
                            ]} />
                        )}
                        <Radky isMobile={isMobile} isDark={isDark} skryjPrazdne>
                            {odkryto && <Radek isDark={isDark} isMobile={isMobile} popis="Číslo dokladu" hodnota={citlive(z.cislo_dokladu)} mono />}
                            {odkryto && <Radek isDark={isDark} isMobile={isMobile} popis="Číslo ŘP" hodnota={citlive(z.cislo_ridicskeho_prukazu)} mono />}
                            <Radek isDark={isDark} isMobile={isMobile} popis="Státní příslušnost" hodnota={z.statni_prislusnost?.nazev} />
                            {odkryto && <Radek isDark={isDark} isMobile={isMobile} popis="Zdrav. pojišťovna" hodnota={citlive(z.pojistovna ? `${z.pojistovna.kod} – ${z.pojistovna.nazev}` : null)} />}
                            {odkryto && <Radek isDark={isDark} isMobile={isMobile} popis="Bankovní účet" hodnota={citlive(z.bankovni_ucet)} mono />}
                            {/* Trojstav: null = dotazník se na to neptal — pomlčka, ne „ne". */}
                            <Radek isDark={isDark} isMobile={isMobile} popis="Prohlášení k dani"
                                hodnota={z.prohlaseni_k_dani == null ? null : (z.prohlaseni_k_dani ? 'Ano' : 'Ne')} />
                        </Radky>
                    </>
                )}
            </Sekce>

            <Sekce nazev="Bydliště" isDark={isDark} akce={upravit('adresa')}>
                {upravuje === 'adresa' ? (
                    <Formular isMobile={isMobile}>
                        <Pole label="Ulice a č.p." isDark={isDark} siroky><Vstup isDark={isDark} value={form.adresa_ulice} onChange={e => setPole('adresa_ulice', e.target.value)} /></Pole>
                        <Pole label="Město" isDark={isDark}><Vstup isDark={isDark} value={form.adresa_mesto} onChange={e => setPole('adresa_mesto', e.target.value)} /></Pole>
                        <Pole label="PSČ" isDark={isDark}><Vstup isDark={isDark} value={form.adresa_psc} onChange={e => setPole('adresa_psc', e.target.value)} /></Pole>
                        <Pole label="Stát" isDark={isDark}><Vstup isDark={isDark} value={form.adresa_stat} onChange={e => setPole('adresa_stat', e.target.value)} /></Pole>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <Zaskrtavatko isDark={isDark} checked={kontaktniStejna} onChange={setKontaktniStejna} label="Kontaktní adresa shodná s trvalou" />
                        </div>
                        {!kontaktniStejna && (<>
                            <Pole label="Kontaktní adresa — ulice a č.p." isDark={isDark} siroky><Vstup isDark={isDark} value={form.kontaktni_adresa_ulice} onChange={e => setPole('kontaktni_adresa_ulice', e.target.value)} /></Pole>
                            <Pole label="Město" isDark={isDark}><Vstup isDark={isDark} value={form.kontaktni_adresa_mesto} onChange={e => setPole('kontaktni_adresa_mesto', e.target.value)} /></Pole>
                            <Pole label="PSČ" isDark={isDark}><Vstup isDark={isDark} value={form.kontaktni_adresa_psc} onChange={e => setPole('kontaktni_adresa_psc', e.target.value)} /></Pole>
                            <Pole label="Stát" isDark={isDark}><Vstup isDark={isDark} value={form.kontaktni_adresa_stat} onChange={e => setPole('kontaktni_adresa_stat', e.target.value)} /></Pole>
                        </>)}
                        {tl(['adresa_ulice', 'adresa_mesto', 'adresa_psc', 'adresa_stat', 'kontaktni_adresa_ulice', 'kontaktni_adresa_mesto', 'kontaktni_adresa_psc', 'kontaktni_adresa_stat'])}
                    </Formular>
                ) : (() => {
                    const trvala = [z.adresa_ulice, [z.adresa_psc, z.adresa_mesto].filter(Boolean).join(' '), z.adresa_stat].filter(Boolean).join(', ');
                    const kontaktni = [z.kontaktni_adresa_ulice, [z.kontaktni_adresa_psc, z.kontaktni_adresa_mesto].filter(Boolean).join(' '), z.kontaktni_adresa_stat].filter(Boolean).join(', ');
                    return (
                        <Radky isMobile={isMobile}>
                            <Radek isDark={isDark} isMobile={isMobile} popis="Trvalé bydliště" hodnota={citlive(trvala)} siroky />
                            {kontaktni && kontaktni !== trvala && <Radek isDark={isDark} isMobile={isMobile} popis="Kontaktní adresa" hodnota={citlive(kontaktni)} siroky />}
                        </Radky>
                    );
                })()}
            </Sekce>

            <Sekce nazev="Vzdělání a rodinný stav" isDark={isDark} akce={upravit('vzdelani')}>
                {upravuje === 'vzdelani' ? (
                    <Formular isMobile={isMobile}>
                        <Pole label="Rodinný stav" isDark={isDark}>
                            <Vyber isDark={isDark} value={form.rodinny_stav} onChange={v => setPole('rodinny_stav', v)} options={vOpcich(RODINNY_STAV)} />
                        </Pole>
                        <Pole label="Nejvyšší vzdělání" isDark={isDark}>
                            <Vyber isDark={isDark} value={form.vzdelani} options={vOpcich(VZDELANI)}
                                onChange={v => setForm(f => ({ ...f, vzdelani: v, ...(v === 'Základní' ? { vzdelani_kod: '', obor_vzdelani: '' } : {}) }))} />
                        </Pole>
                        {form.vzdelani && form.vzdelani !== 'Základní' && (
                            <div style={{ gridColumn: '1 / -1' }}>
                                <KkovSelect vzdelani={form.vzdelani} value={form.vzdelani_kod}
                                    onChange={(kod, obor) => setForm(f => ({ ...f, vzdelani_kod: kod, ...(obor ? { obor_vzdelani: obor } : {}) }))} />
                            </div>
                        )}
                        {tl(['rodinny_stav', 'vzdelani', 'obor_vzdelani', 'vzdelani_kod'])}
                    </Formular>
                ) : (
                    <Radky isMobile={isMobile}>
                        <Radek isDark={isDark} isMobile={isMobile} popis="Rodinný stav" hodnota={citlive(z.rodinny_stav)} />
                        <Radek isDark={isDark} isMobile={isMobile} popis="Vzdělání" hodnota={z.vzdelani} />
                        <Radek isDark={isDark} isMobile={isMobile} popis="Obor" hodnota={z.obor_vzdelani} />
                        <Radek isDark={isDark} isMobile={isMobile} popis="Kód KKOV" hodnota={z.vzdelani_kod} mono />
                    </Radky>
                )}
            </Sekce>

            {(!!z.typ_duchodu || upravuje === 'duchod') && (
                <Sekce nazev="Důchod" isDark={isDark} akce={upravit('duchod')}>
                    {upravuje === 'duchod' ? (
                        <Formular isMobile={isMobile}>
                            <Pole label="Typ důchodu" isDark={isDark}>
                                <Vyber isDark={isDark} value={form.typ_duchodu} onChange={v => setPole('typ_duchodu', v)} options={vOpcich(DUCHODY)} placeholder="Žádný" />
                            </Pole>
                            {form.typ_duchodu && (<>
                                <Pole label="Datum důchodu" isDark={isDark}><Vstup isDark={isDark} type="date" value={form.datum_duchodu} onChange={e => setPole('datum_duchodu', e.target.value)} /></Pole>
                                <Zaskrtavatko isDark={isDark} checked={form.predcasny_duchod} onChange={v => setPole('predcasny_duchod', v)} label="Předčasný důchod" />
                                <Zaskrtavatko isDark={isDark} checked={form.snizeny_vek} onChange={v => setPole('snizeny_vek', v)} label="Snížený věk" />
                            </>)}
                            {tl(['typ_duchodu', 'datum_duchodu', 'predcasny_duchod', 'snizeny_vek'])}
                        </Formular>
                    ) : (
                        <Radky isMobile={isMobile}>
                            <Radek isDark={isDark} isMobile={isMobile} popis="Typ" hodnota={citlive(z.typ_duchodu)} />
                            <Radek isDark={isDark} isMobile={isMobile} popis="Od" hodnota={citlive(datum(z.datum_duchodu))} />
                            <Radek isDark={isDark} isMobile={isMobile} popis="Předčasný" hodnota={citlive(z.predcasny_duchod ? 'Ano' : 'Ne')} />
                            <Radek isDark={isDark} isMobile={isMobile} popis="Snížený věk" hodnota={citlive(z.snizeny_vek ? 'Ano' : 'Ne')} />
                        </Radky>
                    )}
                </Sekce>
            )}

            {(!!z.zdravotni_omezeni_typ || upravuje === 'zdravotni') && (
                <Sekce nazev="Zdravotní omezení" isDark={isDark} akce={upravit('zdravotni')}>
                    {upravuje === 'zdravotni' ? (
                        <Formular isMobile={isMobile}>
                            <Pole label="Typ omezení" isDark={isDark}>
                                <Vyber isDark={isDark} value={form.zdravotni_omezeni_typ} onChange={v => setPole('zdravotni_omezeni_typ', v)} options={vOpcich(ZDR_OMEZENI)} placeholder="Žádné" />
                            </Pole>
                            {form.zdravotni_omezeni_typ && (<>
                                <Pole label="Od" isDark={isDark}><Vstup isDark={isDark} type="date" value={form.zdravotni_omezeni_od} onChange={e => setPole('zdravotni_omezeni_od', e.target.value)} /></Pole>
                                <Pole label="Do" isDark={isDark}><Vstup isDark={isDark} type="date" value={form.zdravotni_omezeni_do} onChange={e => setPole('zdravotni_omezeni_do', e.target.value)} /></Pole>
                            </>)}
                            <Zaskrtavatko isDark={isDark} checked={form.ztp_p} onChange={v => setPole('ztp_p', v)} label="ZTP/P" />
                            {tl(['zdravotni_omezeni_typ', 'zdravotni_omezeni_od', 'zdravotni_omezeni_do', 'ztp_p'])}
                        </Formular>
                    ) : (
                        <Radky isMobile={isMobile}>
                            <Radek isDark={isDark} isMobile={isMobile} popis="Typ" hodnota={citlive(z.zdravotni_omezeni_typ)} />
                            <Radek isDark={isDark} isMobile={isMobile} popis="ZTP/P" hodnota={citlive(z.ztp_p ? 'Ano' : 'Ne')} />
                            <Radek isDark={isDark} isMobile={isMobile} popis="Od" hodnota={citlive(datum(z.zdravotni_omezeni_od))} />
                            <Radek isDark={isDark} isMobile={isMobile} popis="Do" hodnota={citlive(datum(z.zdravotni_omezeni_do))} />
                        </Radky>
                    )}
                </Sekce>
            )}

            <Sekce nazev="Právní stav" isDark={isDark} akce={upravit('pravni')}>
                {upravuje === 'pravni' ? (
                    <Formular isMobile={isMobile}>
                        <div className="flex flex-wrap" style={{ gridColumn: '1 / -1', gap: 6 }}>
                            {[{ value: 'nemam', label: 'Nemám' }, { value: 'exekuce', label: 'Exekuce' }, { value: 'insolvence', label: 'Insolvence' }].map(o => {
                                const sel = form.insolvence_exekuce === o.value;
                                return (
                                    <button key={o.value} type="button" onClick={() => setPole('insolvence_exekuce', o.value)}
                                        className="rounded-lg text-[13px]"
                                        style={{
                                            height: isMobile ? 40 : 32, padding: '0 14px', cursor: 'pointer',
                                            background: sel ? `${SECTION_COLOR}1f` : 'transparent', color: sel ? SECTION_COLOR : 'var(--text-secondary)',
                                            border: `1px solid ${sel ? `${SECTION_COLOR}88` : (isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)')}`,
                                        }}>
                                        {o.label}
                                    </button>
                                );
                            })}
                        </div>
                        {tl(['insolvence_exekuce'])}
                    </Formular>
                ) : (
                    <Radky isMobile={isMobile}>
                        <Radek isDark={isDark} isMobile={isMobile} popis="Insolvence / exekuce"
                            hodnota={citlive(z.insolvence_exekuce && z.insolvence_exekuce !== 'nemam'
                                ? z.insolvence_exekuce.charAt(0).toUpperCase() + z.insolvence_exekuce.slice(1) : 'Nemám')} />
                        <Radek isDark={isDark} isMobile={isMobile} popis="Vyživované osoby"
                            hodnota={z.vyzivovane_osoby?.length ? String(z.vyzivovane_osoby.length) : 'žádné'} />
                    </Radky>
                )}
                {odkryto && z.vyzivovane_osoby?.length > 0 && upravuje !== 'pravni' && (
                    <div style={{ marginTop: 4 }}>
                        {z.vyzivovane_osoby.map((o, i) => (
                            <Radek key={i} isDark={isDark} isMobile={isMobile} popis={o.jmeno_prijmeni}
                                hodnota={[o.rodne_cislo, o.ztp_p ? 'ZTP/P' : null].filter(Boolean).join(' · ') || null} mono />
                        ))}
                    </div>
                )}
            </Sekce>

            {(!!z.poznamka || upravuje === 'poznamka' || muzuEditovat) && (
                <Sekce nazev="Poznámka" isDark={isDark} akce={upravit('poznamka')}>
                    {upravuje === 'poznamka' ? (
                        <Formular isMobile={isMobile}>
                            <textarea value={form.poznamka} onChange={e => setPole('poznamka', e.target.value)} rows={3}
                                style={{ ...inputStyl(isDark), gridColumn: '1 / -1', resize: 'vertical' }} />
                            {tl(['poznamka'])}
                        </Formular>
                    ) : (
                        <p className="text-[13px] whitespace-pre-wrap" style={{ color: z.poznamka ? 'var(--text-primary)' : mut, padding: '6px 0' }}>
                            {z.poznamka || '—'}
                        </p>
                    )}
                </Sekce>
            )}

            {muzuEditovat && z.stav !== 'neaktivni' && !isMobile && (
                <div style={{ marginTop: 28, paddingTop: 14, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                    <Tlacitko isDark={isDark} nebezpecne onClick={() => setDeaktivace(true)}>
                        <UserX size={14} /> Ukončit pracovní poměr…
                    </Tlacitko>
                </div>
            )}

            {muzuEditovat && z.stav === 'neaktivni' && <OdkazKDokumentum z={z} isDark={isDark} />}
        </>
    );

    return (
        <div className="flex flex-col flex-1 min-h-0">
            {hlavicka}
            <div style={{
                flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain',
                padding: isMobile ? '6px 14px 18px' : '8px 18px 24px',
                ...(rezervaDole ? { paddingBottom: `calc(18px + ${rezervaDole})` } : null),
            }}>
                {tab === 'karta' && karta}
                {tab === 'kariera' && (
                    <KarieraMzda z={z} isDark={isDark} isMobile={isMobile} muzuEditovat={muzuEditovat}
                        onZmena={() => { nacti(); onDataChanged?.(); }} />
                )}
                {/* Dokumenty zůstávají namontované, ať se po přepnutí záložek nenačítají znovu */}
                <div style={{ display: tab === 'dokumenty' ? 'block' : 'none' }}>
                    <DokumentyZamestnance zamestnanecId={z.id} isDark={isDark} isMobile={isMobile}
                        muzuEditovat={muzuEditovat} aktivni={tab === 'dokumenty'}
                        onPocet={setPocetDokumentu} onZmena={() => { nacti(); onDataChanged?.(); }} />
                </div>
            </div>

            {deaktivace && (
                <DeaktivaceModal z={z} isDark={isDark} onZavri={() => setDeaktivace(false)}
                    onHotovo={async () => { await nacti(); onDataChanged?.(); }} />
            )}
        </div>
    );
}

/**
 * Bývalý zaměstnanec: přihlášení má vypnuté, ke svým dokumentům se dostane
 * osobním odkazem z e-mailu. Nový dokument mu odkaz pošle sám (při nahrání),
 * tady ho jde poslat ručně — třeba když si o dokumenty řekne.
 */
function OdkazKDokumentum({ z, isDark }) {
    const [bezi, setBezi] = useState(false);
    const [vysledek, setVysledek] = useState(null);
    const poslat = async () => {
        setBezi(true); setVysledek(null);
        try {
            const r = await fetch(`/api/zamestnanci/${z.id}/odkaz-dokumentu`, {
                method: 'POST', headers: { Accept: 'application/json', 'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content },
            });
            const d = await r.json().catch(() => ({}));
            setVysledek(r.ok ? { typ: 'ok', text: `Odkaz odešel na ${d.email}. Platí 30 dní.` } : { typ: 'chyba', text: d.error || 'Odkaz se nepodařilo poslat.' });
        } catch {
            setVysledek({ typ: 'chyba', text: 'Odkaz se nepodařilo poslat.' });
        }
        setBezi(false);
    };
    return (
        <div style={{ marginTop: 28, paddingTop: 14, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }} className="space-y-3">
            <p className="text-[13px]" style={{ color: muted(isDark), lineHeight: 1.5, margin: 0 }}>
                Bývalý zaměstnanec — do systému se nepřihlásí. Nové dokumenty mu chodí e-mailem s osobním odkazem,
                kde si je stáhne a podepíše.
            </p>
            <Tlacitko isDark={isDark} onClick={poslat} disabled={bezi}>{bezi ? 'Posílám…' : 'Poslat odkaz na dokumenty'}</Tlacitko>
            <Hlaska hlaska={vysledek} />
        </div>
    );
}

/**
 * Ukončení pracovního poměru — rychlá akce se začátkem a koncem → modal
 * (norma §1f). Odchozí přijde o přístup do systému, takže mu podepsané
 * dokumenty odejdou mailem; náhled, kolik jich je, se tahá při otevření.
 */
function DeaktivaceModal({ z, isDark, onZavri, onHotovo }) {
    const [datumVystupu, setDatumVystupu] = useState('');
    const [archiv, setArchiv] = useState(null);
    const [email, setEmail] = useState('');
    const [poslat, setPoslat] = useState(true);
    const [bezi, setBezi] = useState(false);
    const [vysledek, setVysledek] = useState(null);

    useEffect(() => {
        fetch(`/api/zamestnanci/${z.id}/archiv-dokumentu`, { headers: { Accept: 'application/json' } })
            .then(r => (r.ok ? r.json() : null))
            .then(d => { if (d) { setArchiv(d); setEmail(d.email || ''); setPoslat(d.pocet > 0); } })
            .catch(() => {});
    }, [z.id]);

    const potvrdit = async () => {
        setBezi(true); setVysledek(null);
        const res = await fetch(`/api/zamestnanci/${z.id}`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ stav: 'neaktivni', datum_vystupu: datumVystupu }),
        });
        if (!res.ok) { setBezi(false); setVysledek({ typ: 'chyba', text: 'Nepodařilo se uložit.' }); return; }

        // Deaktivace prošla — teď dokumenty. Když odeslání selže, dialog zůstane
        // otevřený s chybou, ať jde zkusit znovu; zaměstnanec už je neaktivní.
        if (poslat && archiv?.pocet > 0 && email) {
            try {
                const r = await fetch(`/api/zamestnanci/${z.id}/archiv-dokumentu`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    body: JSON.stringify({ email }),
                });
                const d = await r.json().catch(() => ({}));
                if (!r.ok) {
                    setVysledek({ typ: 'chyba', text: `Zaměstnanec je neaktivní, ale dokumenty se nepodařilo odeslat: ${d.error || 'neznámá chyba'}` });
                    await onHotovo(); setBezi(false); return;
                }
                setVysledek({ typ: 'ok', text: `Hotovo. Na ${d.email} odešlo ${d.odeslano} podepsaných dokumentů.` });
                await onHotovo(); setBezi(false); return;
            } catch (e) {
                setVysledek({ typ: 'chyba', text: `Zaměstnanec je neaktivní, ale odeslání dokumentů selhalo: ${e.message}` });
                await onHotovo(); setBezi(false); return;
            }
        }
        await onHotovo();
        onZavri();
    };

    const hotovo = vysledek?.typ === 'ok';
    const mut = muted(isDark);
    return (
        <ModalShell onClose={onZavri} isDark={isDark} nadpis={`Ukončit pracovní poměr — ${celeJmeno(z)}`} ikona={UserX} barva="#EF4444"
            paticka={(
                <div className="flex gap-2 justify-end">
                    <Tlacitko isDark={isDark} vyska={40} onClick={onZavri}>{hotovo ? 'Zavřít' : 'Zrušit'}</Tlacitko>
                    {!hotovo && (
                        <button type="button" onClick={potvrdit}
                            disabled={!datumVystupu || bezi || (poslat && archiv?.pocet > 0 && !email)}
                            className="rounded-lg font-semibold disabled:opacity-50"
                            style={{ height: 40, padding: '0 16px', background: '#EF4444', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13 }}>
                            {bezi ? 'Ukládám…' : 'Ukončit'}
                        </button>
                    )}
                </div>
            )}>
            <div style={{ padding: 16 }} className="space-y-4">
                <Pole label="Datum výstupu *" isDark={isDark}>
                    <Vstup isDark={isDark} type="date" value={datumVystupu} onChange={e => setDatumVystupu(e.target.value)} />
                </Pole>
                <p className="text-xs" style={{ color: mut, margin: 0, lineHeight: 1.5 }}>
                    Přihlášení do systému se hned vypne. Dokumenty, které mu později nahraješ, mu přijdou e-mailem
                    s osobním odkazem, kde si je stáhne a podepíše.
                </p>
                {archiv && archiv.pocet === 0 ? (
                    <p className="text-xs" style={{ color: mut }}>Nemá žádné podepsané dokumenty — není co archivovat.</p>
                ) : (
                    <div>
                        <Zaskrtavatko isDark={isDark} checked={poslat} onChange={setPoslat}
                            label={`Odeslat podepsané dokumenty${archiv ? ` (${archiv.pocet})` : ''} na kontaktní e-mail`} />
                        <p className="text-xs" style={{ color: mut, marginBottom: 8 }}>Ať je má hned u sebe — pozdější dokumenty mu půjdou odkazem.</p>
                        {poslat && (
                            <>
                                <Vstup isDark={isDark} type="email" placeholder="kontaktni@email.cz" value={email} onChange={e => setEmail(e.target.value)} />
                                {archiv?.email_je_firemni && email === archiv.email && (
                                    <p className="text-[11px]" style={{ color: '#f87171', marginTop: 4 }}>Tohle je firemní adresa — po odchodu na ni nedosáhne. Vyplň soukromou.</p>
                                )}
                            </>
                        )}
                    </div>
                )}
                <Hlaska hlaska={vysledek} />
            </div>
        </ModalShell>
    );
}
